import React, { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNotification } from "./Notifications/NotificationContext";

interface InstagramPost {
  id: string;
  media_type: string;
  media_url: string;
  permalink: string;
  caption?: string;
}

const InstagramFeed: React.FC = () => {
  const [posts, setPosts] = useState<InstagramPost[]>([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const { addNotification } = useNotification();

  const API_URL = process.env.REACT_APP_INSTAGRAM_API_URL;
  const ACCESS_TOKEN = process.env.REACT_APP_INSTAGRAM_ACCESS_TOKEN;

  // ✅ Empêcher plusieurs fetch grâce à un useRef
  const hasFetched = useRef(false);

  useEffect(() => {
    // ✅ Vérifie si le fetch a déjà été fait
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchInstagramPosts = async () => {
      try {
        const response = await fetch(
          `${API_URL}/me/media?fields=id,media_type,media_url,permalink,caption&access_token=${ACCESS_TOKEN}`
        );

        if (!response.ok) {
          throw new Error(
            `Erreur ${response.status}: Impossible de récupérer les données.`
          );
        }

        const data = await response.json();
        if (data && data.data) {
          setPosts(data.data.slice(0, 5));
        } else {
          throw new Error("Aucune donnée reçue depuis Instagram.");
        }
      } catch (err) {
        console.error(
          "Erreur lors de la récupération des posts Instagram :",
          err
        );
        addNotification(
          "Erreur : Impossible de récupérer les publications Instagram.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchInstagramPosts();
  }, []); // ✅ Aucune dépendance pour ne pas re-fetch

  return (
    <section className="instagram-feed">
      <h2>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {t("instagram.followUs")}
          <span className="highlight"> INSTAGRAM</span>
        </motion.div>
      </h2>

      {loading && (
        <motion.p className="instagram-loading">
          {t("instagram.loading", "Chargement des publications Instagram...")}
        </motion.p>
      )}

      {!loading && posts.length === 0 && (
        <motion.p className="instagram-no-posts">
          {t("instagram.noPosts", "Aucune publication Instagram trouvée.")}
        </motion.p>
      )}

      {!loading && posts.length > 0 && (
        <div className="instagram-grid">
          {posts.map((post) => (
            <motion.div className="instagram-post" key={post.id}>
              <a
                href={post.permalink}
                target="_blank"
                rel="noopener noreferrer"
              >
                <motion.img
                  src={post.media_url}
                  alt={post.caption || "Instagram Post"}
                  className="instagram-image"
                />
              </a>
              {post.media_type === "VIDEO" && (
                <div className="instagram-video-indicator"></div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </section>
  );
};

export default InstagramFeed;
